import { Avatar } from "antd";
import { Loader2, ShieldCheck, Users } from "lucide-react";

function AccountPicker({ accounts, onSelect, selectingId }) {
  return (
    <div className="w-full space-y-3">
      <p className="text-center text-xs font-medium uppercase tracking-widest text-muted-foreground">
        Choose a profile
      </p>

      <div className="flex flex-col gap-2">
        {accounts.map((account) => {
          const isSelecting = selectingId === account.account_id;
          const disabled = Boolean(selectingId) || !account.is_active;
          const isOwner = account.is_owner;

          return (
            <button
              key={account.account_id}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(account)}
              className={`group relative flex w-full items-center gap-4 overflow-hidden rounded-2xl px-4 py-3.5 text-left transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-40 ${
                isSelecting
                  ? isOwner
                    ? "bg-amber-500/10 shadow-[0_0_28px_-4px_rgba(245,158,11,0.4)]"
                    : "bg-primary/10 shadow-[0_0_28px_-4px_hsl(var(--primary)/0.4)]"
                  : "bg-card/40 hover:bg-muted/40"
              }`}
              style={{
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                border: isSelecting
                  ? isOwner
                    ? "1px solid rgba(245,158,11,0.45)"
                    : "1px solid hsl(var(--primary)/0.45)"
                  : "1px solid hsl(var(--border)/0.4)",
              }}
            >
              {/* glowing left accent bar */}
              <span
                className={`absolute left-0 top-3 bottom-3 w-0.5 rounded-full transition-all duration-300 ${
                  isSelecting
                    ? isOwner
                      ? "bg-amber-400 shadow-[0_0_12px_2px_rgba(245,158,11,0.7)]"
                      : "bg-primary shadow-[0_0_12px_2px_hsl(var(--primary))]"
                    : isOwner
                      ? "bg-amber-400/40 group-hover:bg-amber-400/80 group-hover:shadow-[0_0_10px_1px_rgba(245,158,11,0.5)]"
                      : "bg-primary/30 group-hover:bg-primary/70 group-hover:shadow-[0_0_10px_1px_hsl(var(--primary)/0.5)]"
                }`}
              />

              {/* avatar */}
              <div className="relative ml-2 shrink-0">
                <Avatar
                  size={46}
                  src={account.logo_url || undefined}
                  className={`!text-base !font-bold ring-2 transition-transform duration-200 ${
                    isSelecting
                      ? "scale-105 ring-amber-400/60"
                      : isOwner
                        ? "ring-amber-400/30 group-hover:scale-105 group-hover:ring-amber-400/60"
                        : "ring-primary/20 group-hover:scale-105 group-hover:ring-primary/50"
                  }`}
                  style={{ backgroundColor: isOwner ? "#92400e" : "#7c3aed" }}
                >
                  {account.name?.[0]?.toUpperCase()}
                </Avatar>
                {isSelecting && (
                  <span className="absolute inset-0 flex items-center justify-center rounded-full bg-background/50">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  </span>
                )}
              </div>

              {/* info */}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground">
                  {account.name}
                </p>
                <p className="mt-0.5 flex items-center gap-1 text-[11px]">
                  {isOwner ? (
                    <>
                      <ShieldCheck className="h-3 w-3 text-amber-400" />
                      <span className="font-medium text-amber-400">Owner</span>
                    </>
                  ) : (
                    <>
                      <Users className="h-3 w-3 text-primary/60" />
                      <span className="text-muted-foreground">
                        Member
                      </span>
                    </>
                  )}
                  {!account.is_active && (
                    <span className="ml-1 text-amber-500/80">
                      · Pending approval
                    </span>
                  )}
                </p>
              </div>

              {/* right arrow */}
              {!isSelecting && (
                <svg
                  className={`h-4 w-4 shrink-0 transition-all duration-200 ${
                    isOwner
                      ? "text-amber-400/40 group-hover:translate-x-0.5 group-hover:text-amber-400/80"
                      : "text-primary/30 group-hover:translate-x-0.5 group-hover:text-primary/70"
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default AccountPicker;
